// assets/backstock-preorder-input.js
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Keeps the Backstock (Pre-order Manager Pro) pre-order input in step with the form's
 * quantity (WHHA-996). The widget flags a line as a pre-order when the quantity exceeds
 * what's in stock (`left`), and re-checks as the quantity changes, but it only runs on
 * product pages. This does the same for the forms it doesn't manage, such as product
 * cards: the input is sent only while the quantity is above `left`.
 *
 * Rendered by snippets/backstock-preorder-input.liquid, which sets the correct initial
 * state server-side, so without JS the starting quantity is still handled.
 */
class BackstockPreorderInput extends Component {
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    const form = this.closest('form');
    if (!form) return;

    // A fresh controller per connect: the old one is aborted on disconnect, and a form
    // re-attached by a section re-render needs its listeners back.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    for (const type of ['input', 'change', ThemeEvents.quantitySelectorUpdate]) {
      form.addEventListener(type, this.#sync, { signal });
    }
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
  }

  #sync = () => {
    // On the PDP the widget removes this input and adds its own, so there is nothing to sync.
    const input = this.querySelector('input[data-mas-backstock-property-label]');
    if (!(input instanceof HTMLInputElement)) return;

    const quantityInput = this.closest('form')?.querySelector('input[name="quantity"]');
    const quantity = quantityInput instanceof HTMLInputElement ? parseInt(quantityInput.value, 10) || 1 : 1;

    input.disabled = !(quantity > Number(this.dataset.left));

    // Where the widget manages this variant (its PDP), its own label already shows the date,
    // so extra forms for the same product, such as the separate "Buy it now" block, hide ours.
    const label = this.querySelector('[mas-backstock-label]');
    if (label instanceof HTMLElement) label.hidden = input.disabled || this.#isWidgetManaged();
  };

  #isWidgetManaged() {
    const variantId = this.closest('form')?.querySelector('input[name="id"]');
    const map = /** @type {any} */ (window).masBackStock?.map;
    if (!(variantId instanceof HTMLInputElement) || !Array.isArray(map)) return false;

    return map.some((entry) => String(entry.id) === variantId.value);
  }
}

if (!customElements.get('backstock-preorder-input')) {
  customElements.define('backstock-preorder-input', BackstockPreorderInput);
}
